import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/context/AuthContext';
import { useLanguage } from '@/context/LanguageContext';
import { apiHelpers } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Switch } from '@/components/ui/switch';
import { toast } from 'sonner';
import { useConfirmDialog } from '@/components/ConfirmDialog';
import {
  Plus, Search, Phone, Mail, MapPin, DollarSign, Trash2,
  UserPlus, ArrowRight, Edit2, X, TrendingUp, Filter,
  Clock, CheckCircle, XCircle, MessageCircle, Loader2, ExternalLink
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const STATUS_CONFIG = {
  new: { label: 'New', color: 'bg-blue-100 text-blue-700', icon: Clock },
  contacted: { label: 'Contacted', color: 'bg-amber-100 text-amber-700', icon: MessageCircle },
  qualified: { label: 'Qualified', color: 'bg-purple-100 text-purple-700', icon: TrendingUp },
  converted: { label: 'Converted', color: 'bg-green-100 text-green-700', icon: CheckCircle },
  lost: { label: 'Lost', color: 'bg-red-100 text-red-700', icon: XCircle },
};

const SOURCE_OPTIONS = ['Website', 'Referral', 'Social Media', 'Google', 'Phone Call', 'Door-to-Door', 'Other'];

const LeadsPage = () => {
  const { organization, user } = useAuth();
  const { t } = useLanguage();
  const navigate = useNavigate();
  const { showConfirm, ConfirmDialogComponent } = useConfirmDialog();

  const [leads, setLeads] = useState([]);
  const [stats, setStats] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editingLead, setEditingLead] = useState(null);
  const [showConvertModal, setShowConvertModal] = useState(false);
  const [convertingLead, setConvertingLead] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [form, setForm] = useState({
    name: '', email: '', phone: '', source: '', notes: '', address: '', service_interest: '', estimated_value: ''
  });
  const [convertForm, setConvertForm] = useState({
    create_site_visit: true, service_type: '', scheduled_date: new Date().toISOString().split('T')[0], scheduled_time: '09:00'
  });

  const primaryColor = organization?.branding?.primary_color || '#f59e0b';

  const loadData = useCallback(async () => {
    if (!organization?.id) return;
    setIsLoading(true);
    try {
      const [leadsRes, statsRes] = await Promise.all([
        apiHelpers.getLeads(organization.id, statusFilter),
        apiHelpers.getLeadStats(organization.id),
      ]);
      setLeads(leadsRes.data || []);
      setStats(statsRes.data);
    } catch (err) {
      if (err.response?.status === 403) {
        toast.error('Leads feature is not enabled');
        navigate('/dashboard');
      } else {
        toast.error('Failed to load leads');
      }
    } finally {
      setIsLoading(false);
    }
  }, [organization?.id, statusFilter, navigate]);

  useEffect(() => { loadData(); }, [loadData]);

  const filteredLeads = leads.filter(l => {
    if (!searchQuery) return true;
    const q = searchQuery.toLowerCase();
    return l.name?.toLowerCase().includes(q) || l.email?.toLowerCase().includes(q) || l.phone?.includes(q);
  });

  const handleSave = async () => {
    if (!form.name.trim()) { toast.error('Name is required'); return; }
    setIsSubmitting(true);
    try {
      const payload = { ...form, estimated_value: form.estimated_value ? parseFloat(form.estimated_value) : undefined };
      if (editingLead) {
        await apiHelpers.updateLead(organization.id, editingLead.id, payload, user.id, user.name);
        toast.success('Lead updated');
      } else {
        await apiHelpers.createLead(organization.id, payload, user.id, user.name);
        toast.success('Lead created');
      }
      setShowForm(false);
      setEditingLead(null);
      setForm({ name: '', email: '', phone: '', source: '', notes: '', address: '', service_interest: '', estimated_value: '' });
      loadData();
    } catch { toast.error('Failed to save lead'); }
    finally { setIsSubmitting(false); }
  };

  const handleDelete = async (lead) => {
    const ok = await showConfirm({ title: 'Delete Lead?', description: `Remove "${lead.name}" permanently.`, variant: 'danger', confirmText: 'Delete', destructive: true });
    if (!ok) return;
    try {
      await apiHelpers.deleteLead(organization.id, lead.id, user.id);
      toast.success('Lead deleted');
      loadData();
    } catch { toast.error('Failed to delete'); }
  };

  const handleStatusChange = async (lead, newStatus) => {
    try {
      await apiHelpers.updateLead(organization.id, lead.id, { status: newStatus }, user.id, user.name);
      toast.success(`Status: ${newStatus}`);
      loadData();
    } catch { toast.error('Failed to update status'); }
  };

  const handleConvert = async () => {
    if (!convertingLead) return;
    setIsSubmitting(true);
    try {
      const res = await apiHelpers.convertLead(organization.id, convertingLead.id, convertForm, user.id, user.name);
      toast.success(res.data.message);
      setShowConvertModal(false);
      setConvertingLead(null);
      loadData();
      if (res.data.client_id) navigate(`/clients/${res.data.client_id}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to convert');
    } finally { setIsSubmitting(false); }
  };

  const openEdit = (lead) => {
    setEditingLead(lead);
    setForm({
      name: lead.name || '', email: lead.email || '', phone: lead.phone || '',
      source: lead.source || '', notes: lead.notes || '', address: lead.address || '',
      service_interest: lead.service_interest || '', estimated_value: lead.estimated_value || '',
    });
    setShowForm(true);
  };

  const openConvert = (lead) => {
    setConvertingLead(lead);
    setConvertForm({
      create_site_visit: true,
      service_type: lead.service_interest || '',
      scheduled_date: new Date().toISOString().split('T')[0],
      scheduled_time: '09:00',
    });
    setShowConvertModal(true);
  };

  return (
    <div className="space-y-6 pb-24" data-testid="leads-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-bold text-foreground">Leads</h1>
          <p className="text-muted-foreground mt-1">Track and convert potential customers</p>
        </div>
        <Button
          onClick={() => { setEditingLead(null); setForm({ name:'',email:'',phone:'',source:'',notes:'',address:'',service_interest:'',estimated_value:'' }); setShowForm(true); }}
          className="h-12 px-6 font-bold rounded-xl shadow-sm"
          style={{ backgroundColor: primaryColor, color: '#000' }}
          data-testid="new-lead-btn"
        >
          <Plus className="w-5 h-5 mr-2" /> New Lead
        </Button>
      </div>

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-3" data-testid="lead-stats">
          {Object.entries(STATUS_CONFIG).map(([key, cfg]) => {
            const s = stats.by_status?.[key] || { count: 0, value: 0 };
            return (
              <button key={key} onClick={() => setStatusFilter(statusFilter === key ? '' : key)}
                className={`p-3 rounded-xl text-center transition-all border-2 ${statusFilter === key ? 'border-blue-500 shadow-md' : 'border-transparent'}`}
                style={{ backgroundColor: statusFilter === key ? undefined : '#f9fafb' }}
                data-testid={`lead-stat-${key}`}
              >
                <p className="text-2xl font-bold">{s.count}</p>
                <p className="text-xs font-medium text-muted-foreground">{cfg.label}</p>
                {s.value > 0 && <p className="text-xs text-green-600 font-medium mt-1">${s.value.toLocaleString()}</p>}
              </button>
            );
          })}
        </div>
      )}

      {/* Search */}
      <div className="flex gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            value={searchQuery} onChange={e => setSearchQuery(e.target.value)}
            placeholder="Search leads by name, email, or phone..."
            className="pl-10 h-11 rounded-xl"
            data-testid="lead-search"
          />
        </div>
        {statusFilter && (
          <Button variant="outline" size="sm" onClick={() => setStatusFilter('')} className="h-11 px-3">
            <X className="w-4 h-4 mr-1" /> Clear
          </Button>
        )}
      </div>

      {/* Leads List */}
      {isLoading ? (
        <div className="flex items-center justify-center py-16">
          <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
        </div>
      ) : filteredLeads.length === 0 ? (
        <div className="text-center py-16 bg-white rounded-2xl border border-gray-100">
          <TrendingUp className="w-16 h-16 text-muted-foreground mx-auto mb-4 opacity-40" />
          <h3 className="text-lg font-semibold mb-2">{statusFilter ? 'No leads with this status' : 'No leads yet'}</h3>
          <p className="text-muted-foreground mb-4">Add your first lead to start tracking potential customers</p>
          <Button onClick={() => setShowForm(true)} style={{ backgroundColor: primaryColor, color: '#000' }}>
            <Plus className="w-4 h-4 mr-2" /> Add Lead
          </Button>
        </div>
      ) : (
        <div className="space-y-2" data-testid="leads-list">
          {filteredLeads.map(lead => {
            const cfg = STATUS_CONFIG[lead.status] || STATUS_CONFIG.new;
            const StatusIcon = cfg.icon;
            return (
              <div key={lead.id}
                className="bg-white rounded-xl border border-gray-100 p-4 hover:shadow-md transition-shadow"
                data-testid={`lead-card-${lead.id}`}
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1.5">
                      <h3 className="font-semibold text-base truncate">{lead.name}</h3>
                      <Badge className={`text-[10px] ${cfg.color}`}>
                        <StatusIcon className="w-3 h-3 mr-0.5" /> {cfg.label}
                      </Badge>
                      {lead.source && (
                        <span className="text-[10px] px-1.5 py-0.5 rounded bg-gray-100 text-gray-500">{lead.source}</span>
                      )}
                    </div>
                    <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-muted-foreground">
                      {lead.email && <span className="flex items-center gap-1"><Mail className="w-3 h-3" /> {lead.email}</span>}
                      {lead.phone && <span className="flex items-center gap-1"><Phone className="w-3 h-3" /> {lead.phone}</span>}
                      {lead.address && <span className="flex items-center gap-1"><MapPin className="w-3 h-3" /> {lead.address}</span>}
                      {lead.estimated_value > 0 && <span className="flex items-center gap-1 text-green-600 font-medium"><DollarSign className="w-3 h-3" /> ${lead.estimated_value.toLocaleString()}</span>}
                      {lead.service_interest && <span className="text-xs bg-blue-50 text-blue-600 px-2 py-0.5 rounded-full">{lead.service_interest}</span>}
                    </div>
                    {lead.notes && <p className="text-xs text-muted-foreground mt-1.5 line-clamp-1">{lead.notes}</p>}
                  </div>
                  <div className="flex items-center gap-1 flex-shrink-0">
                    {lead.status !== 'converted' && lead.status !== 'lost' && (
                      <Select value={lead.status} onValueChange={(v) => handleStatusChange(lead, v)}>
                        <SelectTrigger className="h-8 w-28 text-xs border-gray-200">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="new">New</SelectItem>
                          <SelectItem value="contacted">Contacted</SelectItem>
                          <SelectItem value="qualified">Qualified</SelectItem>
                          <SelectItem value="lost">Lost</SelectItem>
                        </SelectContent>
                      </Select>
                    )}
                    {lead.status !== 'converted' && (
                      <Button size="sm" onClick={() => openConvert(lead)}
                        className="h-8 px-2.5 text-xs font-semibold bg-green-600 hover:bg-green-700 text-white"
                        data-testid={`convert-lead-${lead.id}`}
                      >
                        <UserPlus className="w-3.5 h-3.5 mr-1" /> Convert
                      </Button>
                    )}
                    {lead.status === 'converted' && lead.converted_client_id && (
                      <Button size="sm" variant="outline" onClick={() => navigate(`/clients/${lead.converted_client_id}`)}
                        className="h-8 px-2.5 text-xs border-green-300 text-green-700"
                        data-testid={`view-client-${lead.id}`}
                      >
                        <ExternalLink className="w-3.5 h-3.5 mr-1" /> View Client
                      </Button>
                    )}
                    <Button size="sm" variant="ghost" onClick={() => openEdit(lead)} className="h-8 w-8 p-0">
                      <Edit2 className="w-3.5 h-3.5" />
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => handleDelete(lead)} className="h-8 w-8 p-0 text-red-500 hover:text-red-700">
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Create/Edit Lead Modal */}
      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent className="max-w-lg" data-testid="lead-form-modal">
          <DialogHeader>
            <DialogTitle>{editingLead ? 'Edit Lead' : 'New Lead'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div>
              <Label>{t('name')} *</Label>
              <Input value={form.name} onChange={e => setForm(p => ({...p, name: e.target.value}))} className="mt-1" data-testid="lead-name-input" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>{t('email')}</Label>
                <Input type="email" value={form.email} onChange={e => setForm(p => ({...p, email: e.target.value}))} className="mt-1" />
              </div>
              <div>
                <Label>{t('phone')}</Label>
                <Input type="tel" value={form.phone} onChange={e => setForm(p => ({...p, phone: e.target.value}))} className="mt-1" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Source</Label>
                <Select value={form.source} onValueChange={v => setForm(p => ({...p, source: v}))}>
                  <SelectTrigger className="mt-1"><SelectValue placeholder="Select..." /></SelectTrigger>
                  <SelectContent>
                    {SOURCE_OPTIONS.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Service Interest</Label>
                <Input value={form.service_interest} onChange={e => setForm(p => ({...p, service_interest: e.target.value}))} placeholder="e.g., Concrete, Roofing" className="mt-1" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>{t('address')}</Label>
                <Input value={form.address} onChange={e => setForm(p => ({...p, address: e.target.value}))} className="mt-1" />
              </div>
              <div>
                <Label>Estimated Value ($)</Label>
                <Input type="number" value={form.estimated_value} onChange={e => setForm(p => ({...p, estimated_value: e.target.value}))} placeholder="0" className="mt-1" />
              </div>
            </div>
            <div>
              <Label>{t('notes')}</Label>
              <Textarea value={form.notes} onChange={e => setForm(p => ({...p, notes: e.target.value}))} rows={2} className="mt-1" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowForm(false)}>{t('cancel')}</Button>
            <Button onClick={handleSave} disabled={isSubmitting} style={{ backgroundColor: primaryColor, color: '#000' }}>
              {isSubmitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              {editingLead ? t('save') : 'Add Lead'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Convert Lead Modal */}
      <Dialog open={showConvertModal} onOpenChange={setShowConvertModal}>
        <DialogContent className="max-w-md" data-testid="convert-lead-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <UserPlus className="w-5 h-5 text-green-600" />
              Convert "{convertingLead?.name}" to Customer
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <p className="text-sm text-muted-foreground">
              This will create a new customer record with all the lead's contact information.
            </p>
            <div className="p-3 rounded-lg bg-green-50 border border-green-200">
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-medium text-sm">Also schedule a site visit?</p>
                  <p className="text-xs text-muted-foreground mt-0.5">Get them on the calendar right away</p>
                </div>
                <Switch checked={convertForm.create_site_visit} onCheckedChange={v => setConvertForm(p => ({...p, create_site_visit: v}))} />
              </div>
            </div>
            {convertForm.create_site_visit && (
              <div className="space-y-3 pl-3 border-l-2 border-green-200">
                <div>
                  <Label className="text-xs">{t('service_type_label')}</Label>
                  <Input value={convertForm.service_type} onChange={e => setConvertForm(p => ({...p, service_type: e.target.value}))} placeholder={convertingLead?.service_interest || 'General'} className="mt-1 h-8 text-sm" />
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <Label className="text-xs">{t('date_label')}</Label>
                    <Input type="date" value={convertForm.scheduled_date} onChange={e => setConvertForm(p => ({...p, scheduled_date: e.target.value}))} className="mt-1 h-8 text-sm" />
                  </div>
                  <div>
                    <Label className="text-xs">{t('time_label')}</Label>
                    <Input type="time" value={convertForm.scheduled_time} onChange={e => setConvertForm(p => ({...p, scheduled_time: e.target.value}))} className="mt-1 h-8 text-sm" />
                  </div>
                </div>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowConvertModal(false)}>{t('cancel')}</Button>
            <Button onClick={handleConvert} disabled={isSubmitting} className="bg-green-600 hover:bg-green-700 text-white" data-testid="confirm-convert-btn">
              {isSubmitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <ArrowRight className="w-4 h-4 mr-2" />}
              Convert to Customer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {ConfirmDialogComponent}
    </div>
  );
};

export default LeadsPage;
